import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ModalFooter } from '../../../modal/layout/ModalFooter';
import {
    ModalScrollableBody,
    ModalScrollableFooter,
} from '../../../modal/layout/ModalScrollableLayout';
import { NOTIFICATION_VARIANT, useNotifications } from '../../../notifications/Notifications';
import type { TextAttributeEntry } from '../contracts';
import { useAttributeMutations } from '../graphql/useAttributeMutations';
import type { AttributeModalContentProps } from './types';

interface TextAttributeFormValues {
    textValue: string;
}

export function AttributeTextModalContent({
    entry,
    entityType,
    entityId,
    onClose,
    onAfterMutation,
    onFormStateChange,
    bodyIntro,
    scrollBodyRef,
}: AttributeModalContentProps<TextAttributeEntry>) {
    const formId = `attribute-text-${entry.key}`;
    const { addNotification } = useNotifications();
    const { setText, isMutating } = useAttributeMutations();
    const {
        register,
        handleSubmit,
        reset,
        formState: { isDirty, isSubmitting },
    } = useForm<TextAttributeFormValues>({
        defaultValues: {
            textValue: entry.textValue,
        },
    });
    const isBusy = isSubmitting || isMutating;

    useEffect(() => {
        reset({ textValue: entry.textValue });
    }, [entry.key, entry.textValue, reset]);

    useEffect(() => {
        onFormStateChange({ isDirty, isBusy });
    }, [isBusy, isDirty, onFormStateChange]);

    async function saveText(values: TextAttributeFormValues) {
        const updatedEntry = await setText(
            {
                entityType,
                entityId,
                key: entry.key,
            },
            values.textValue
        );

        if (updatedEntry.valueType !== 'text') {
            throw new Error(
                `setAttributeText returned ${updatedEntry.valueType} for ${entry.key}.`
            );
        }

        reset({ textValue: updatedEntry.textValue });
        await onAfterMutation();
        addNotification({
            message: gettext('SAVED_SUCCESSFULLY'),
            variant: NOTIFICATION_VARIANT.SUCCESS,
            autoDismiss: true,
        });
    }

    return (
        <>
            <ModalScrollableBody ref={scrollBodyRef}>
                {bodyIntro}
                <form
                    id={formId}
                    onSubmit={(event) => void handleSubmit(saveText)(event)}
                    className="space-y-4 px-6"
                >
                    <label
                        className="block text-sm font-semibold text-gray-700"
                        htmlFor={`${formId}-text`}
                    >
                        {gettext('ATTRIBUTE_TEXT_VALUE_LABEL')}
                    </label>
                    <textarea
                        id={`${formId}-text`}
                        rows={10}
                        className="focus:border-primary focus:ring-primary/30 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-inner focus:outline-none focus:ring-2"
                        {...register('textValue')}
                    />
                </form>
            </ModalScrollableBody>
            <ModalScrollableFooter>
                <ModalFooter
                    formId={formId}
                    onClose={onClose}
                    saveButtonType="submit"
                    isLoading={isBusy}
                    saveDisabled={!isDirty}
                />
            </ModalScrollableFooter>
        </>
    );
}
